import { Plus, Clipboard } from "lucide-react"
import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip"
import {
  nodeCategories,
  getNameByNodeType,
  getDescriptionByNodeType,
  nodeCategoryNames,
  nodeTypeNames
} from "@/types/nodeTypes"
import styles from "@/styles/PaneContextMenu.module.css"

interface PaneContextMenuProps {
  id?: string
  top?: number
  left?: number
  right?: number
  bottom?: number
  onCreateNode: (nodeType: string) => void
  isVisible: boolean
  hasCopiedNode?: boolean
  onPaste?: () => void
  onClose?: () => void // Add this prop
}

export default function PaneContextMenu({
  id,
  top,
  left,
  right,
  bottom,
  onCreateNode,
  isVisible,
  hasCopiedNode,
  onPaste,
  onClose
}: PaneContextMenuProps) {
  const [animationClass, setAnimationClass] = useState("")

  useEffect(() => {
    if (isVisible) {
      setAnimationClass(styles.paneContextMenuVisible)
    } else {
      setAnimationClass(styles.paneContextMenuHidden)
      const timer = setTimeout(() => {
        setAnimationClass("")
      }, 90)
      return () => clearTimeout(timer)
    }
  }, [isVisible])

  const getNodeButtons = (categoryName: string) => {
    return nodeCategories[categoryName].map((nodeTypeName) => (
      <div
        key={`${categoryName}-${nodeTypeName}`}
        className={styles.buttonWrapper}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className={styles.nodeButton}
                onClick={() => {
                  onCreateNode(nodeTypeName)
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                {getNameByNodeType(nodeTypeName as nodeTypeNames)}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              {getDescriptionByNodeType(nodeTypeName as nodeTypeNames)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ))
  }

  return (
    <div
      style={{
        top: top !== undefined ? `${top}px` : undefined,
        left: left !== undefined ? `${left}px` : undefined,
        right: right !== undefined ? `${right}px` : undefined,
        bottom: bottom !== undefined ? `${bottom}px` : undefined
      }}
      className={`${styles.paneContextMenu} ${animationClass}`}
    >
      {hasCopiedNode && onPaste && (
        <div
          className="flex items-center gap-2 px-4 py-2 hover:bg-slate-100 dark:hover:bg-gray-700 cursor-pointer"
          onClick={onPaste}
        >
          <Clipboard size={16} />
          <span>Paste from clipboard</span>
        </div>
      )}
      {Object.keys(nodeCategories).map((category) => (
        <div key={category} className={styles.categoryContainer}>
          <h3 className={styles.categoryTitle}>
            {nodeCategoryNames[category as keyof typeof nodeCategories] ||
              category}
          </h3>
          <div className={styles.buttonContainer}>
            {getNodeButtons(category)}
          </div>
        </div>
      ))}
    </div>
  )
}
